"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { SearchCheck, Target, TrendingUp, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const processSteps = [{
  step: "01",
  title: "Free SEO Consultation and Website Audit",
  icon: SearchCheck,
  description: "Everything starts with a comprehensive free SEO audit and consultation. I identify technical issues affecting your search engine rankings, review your existing content, and assess your competitors' landscape. This includes checking site speed, mobile responsiveness, broken links, indexing issues, and on-page elements like metadata.",
  highlights: ["Site speed & mobile checks", "Indexing & broken link review", "Metadata & competitor analysis"]
}, {
  step: "02",
  title: "Custom SEO Strategy Development",
  icon: Target,
  description: "Based on the audit findings, I develop a tailored SEO strategy. This involves in-depth keyword research to identify the terms your customers actually use, thorough competitor analysis, and a prioritised action plan that focuses on high-impact opportunities first. I map out target keywords and content creation, define technical optimisation tasks, and establish a realistic timeline.",
  highlights: ["High-impact keyword research", "Competitor gap analysis", "Prioritised technical roadmaps"]
}, {
  step: "03",
  title: "Implementation and Results",
  icon: TrendingUp,
  description: <>
        With the strategy in place, I begin implementing prioritised SEO improvements across your website. This covers on-page SEO,{" "}
        <Link to="/TechnicalSeoPage" className="text-primary hover:underline underline-offset-2">
          technical SEO improvements
        </Link>
        , and off-page SEO work including ethical link building. Regular monthly reporting and conversion tracking show how each step affects traffic, leads, and conversions over time. No guesswork. No wasted effort. Just structured SEO progress that compounds over time.
      </>,
  highlights: ["On-page & technical fixes", "Ethical link acquisition", "Transparent monthly reporting"]
}];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 24
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: shouldReduceMotion ? 0 : i * 0.15,
        ease: "easeOut"
      }
    })
  };
  return <section id="howmyseoprocessworks" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider px-3 py-1">
            Methodology
          </Badge>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-6">
            How my SEO process works
          </h2>
          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
            Getting results from SEO doesn&apos;t require complexity. My process is straightforward, transparent, and designed to deliver structured progress from day one.
          </p>
        </div>

        {/* Steps Grid with Visual Connecting Line */}
        <div className="relative">
          {/* Subtle connecting line across desktop */}
          <div className="hidden lg:block absolute top-28 left-[15%] right-[15%] h-0.5 bg-border -z-0" aria-hidden="true" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-8 relative z-10">
            {processSteps.map((stepItem, i) => {
            const IconComponent = stepItem.icon;
            return <motion.div key={stepItem.step} data-index={i} custom={i} initial="hidden" whileInView="visible" viewport={{
              once: true,
              amount: 0.2
            }} variants={cardVariants} className="flex flex-col h-full bg-card rounded-2xl p-8 border border-border shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300 relative group">
                  {/* Step Top Meta: Number badge + Icon */}
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <span className="inline-flex items-center justify-center size-12 rounded-xl bg-primary text-primary-foreground font-serif font-bold text-lg shadow-xs">
                      {stepItem.step}
                    </span>
                    <div className="size-12 rounded-xl bg-secondary/80 flex items-center justify-center text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-primary-foreground">
                      <IconComponent className="size-6" />
                    </div>
                  </div>

                  {/* Step Title */}
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold text-card-foreground mb-4">
                    {stepItem.title}
                  </h3>

                  {/* Step Description */}
                  <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed mb-6 flex-1">
                    {stepItem.description}
                  </p>

                  {/* Key Highlights */}
                  <div className="pt-4 border-t border-border/60 mt-auto">
                    <ul className="space-y-2">
                      {stepItem.highlights.map(highlight => <li key={highlight} className="flex items-center gap-2 text-xs sm:text-sm text-foreground/80 font-medium">
                          <CheckCircle2 className="size-4 text-primary shrink-0" />
                          <span>{highlight}</span>
                        </li>)}
                    </ul>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Bottom CTA Block */}
        <div className="mt-16 sm:mt-20 text-center">
          <Button asChild size="lg" className="rounded-full px-8 py-6 font-medium text-base shadow-sm hover:shadow-md transition-[box-shadow,transform]">
            <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2">
              <span>Get a free SEO audit today</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}